import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const SLIDE_MS = 280;
const DISMISS_PX = 96;
const LOCK_AXIS_PX = 16;

type Props = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
};

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Lecteur plein écran : entre depuis la droite, swipe doigt vers la droite pour revenir. */
export function MobileReaderOverlay({ open, onClose, children }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [visible, setVisible] = useState(open);
  const [entered, setEntered] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const dragXRef = useRef(0);
  const trackingRef = useRef(false);
  const draggingRef = useRef(false);
  const lockedRef = useRef<"h" | "v" | null>(null);

  const setDrag = useCallback((value: number) => {
    const width = window.innerWidth || 1;
    const clamped = Math.max(0, Math.min(width, value));
    dragXRef.current = clamped;
    setDragX(clamped);
  }, []);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setDrag(0);
      if (prefersReducedMotion()) {
        setEntered(true);
        return;
      }
      setEntered(false);
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setEntered(true));
      });
      return () => window.cancelAnimationFrame(id);
    }
    setEntered(false);
    const delay = prefersReducedMotion() ? 0 : SLIDE_MS;
    const timer = window.setTimeout(() => {
      setVisible(false);
      setDrag(0);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [open, setDrag]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !open) return;

    const onTouchStart = (e: TouchEvent) => {
      if ((e.target as Element | null)?.closest("[data-mail-no-swipe-back]")) return;
      const touch = e.touches[0];
      if (!touch) return;
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      trackingRef.current = true;
      lockedRef.current = null;
      dragXRef.current = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!trackingRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      if (lockedRef.current == null) {
        if (Math.abs(dx) < LOCK_AXIS_PX && Math.abs(dy) < LOCK_AXIS_PX) return;
        lockedRef.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
        if (lockedRef.current === "v") {
          trackingRef.current = false;
          return;
        }
        draggingRef.current = true;
        setDragging(true);
      }

      if (lockedRef.current !== "h") return;
      if (dx > 0) {
        e.preventDefault();
        setDrag(dx);
      } else {
        setDrag(0);
      }
    };

    const onTouchEnd = () => {
      if (!trackingRef.current && !draggingRef.current) return;
      trackingRef.current = false;
      draggingRef.current = false;
      setDragging(false);
      const width = window.innerWidth || 1;
      const shouldClose = dragXRef.current >= DISMISS_PX || dragXRef.current >= width * 0.28;
      if (shouldClose) {
        onCloseRef.current();
        return;
      }
      setDrag(0);
    };

    panel.addEventListener("touchstart", onTouchStart, { passive: true });
    panel.addEventListener("touchmove", onTouchMove, { passive: false });
    panel.addEventListener("touchend", onTouchEnd, { passive: true });
    panel.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      panel.removeEventListener("touchstart", onTouchStart);
      panel.removeEventListener("touchmove", onTouchMove);
      panel.removeEventListener("touchend", onTouchEnd);
      panel.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [open, setDrag]);

  if (!visible) return null;

  const panelTransform = !open
    ? "translate3d(100%, 0, 0)"
    : dragging || dragX > 0
      ? `translate3d(${dragX}px, 0, 0)`
      : entered
        ? "translate3d(0, 0, 0)"
        : "translate3d(100%, 0, 0)";

  return (
    <div
      className="mobile-reader-overlay-root"
      role="dialog"
      aria-modal="true"
      aria-label="Message"
    >
      <div
        ref={panelRef}
        className="mobile-reader-overlay-panel"
        style={{
          transform: panelTransform,
          transition:
            dragging || prefersReducedMotion()
              ? "none"
              : `transform ${SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
        }}
      >
        {children}
        <div className="mobile-reader-overlay-edge" aria-hidden />
      </div>
    </div>
  );
}
